/**
 * Uploading the one number this product cannot fetch.
 *
 * Shaped after the sales-agent importer, which sat beside it until that was
 * hidden on 2026-10-20 — choose a file, see exactly what was read, then
 * commit — because the two are the same kind of act. Where it differs, it
 * differs for a reason:
 *
 * NO DATE PICKER. The agent file describes one period, so the agent importer
 * has to be told which. This file carries a date on every row, so a range the
 * uploader typed could only ever agree with the file or contradict it — the
 * span is read out and shown back for confirmation instead.
 *
 * COVERAGE IS SHOWN BEFORE COMMITTING, because applying REPLACES the days it
 * covers. Somebody re-sending a corrected March should be told that thirty-one
 * days are about to be rewritten; somebody sending March for the first time
 * should not be alarmed by the same screen.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, AlertTriangle, CheckCircle2, Undo2, CalendarRange, Info } from 'lucide-react';
import { parseOutcomeCsv, METRIC_COLUMNS, type ParsedOutcomeRow } from '@/services/outcomeImport/parseOutcomeCsv';
import type { RowProblem } from '@/services/brokerImport/parseBrokerCsv';
import {
  applyOutcomeImport,
  revertOutcomeImport,
  fetchOutcomeImports,
  fetchExistingCoverage,
  fetchOutcomeBreakdown,
  type OutcomeImportRecord,
  type ExistingCoverage,
  type OutcomePlatformLine,
} from '@/services/outcomeImport/outcomeImportService';
import { logError } from '@/services/logger';

/**
 * A figure the client did not send reads as "not reported", never as zero. They
 * are different facts and the difference is load-bearing: a day with zero sales
 * is a day nobody bought, a day with no sales COLUMN is a day we cannot speak
 * for. Rendering the second as ฿0 would put a number in a model's mouth.
 */
const reported = (n: number | null, fmt: (v: number) => string, missing: string): string =>
  n === null ? missing : fmt(n);

const baht = (n: number): string => `฿${Math.round(n).toLocaleString('en-US')}`;
const count = (n: number): string => n.toLocaleString('en-US');

interface Props {
  workspaceId: string;
  /** False for a viewer: the panel renders read-only with the reason. */
  canEdit: boolean;
}

const SAMPLE = [
  'date,platform,leads,bookings,sales,revenue',
  '2026-01-15,facebook,20,5,1,1450000',
  '2026-01-15,google,18,4,1,1450000',
  '2026-01-15,line,6,2,1,1450000',
  '2026-01-16,facebook,23,6,2,2900000',
  '2026-01-16,google,19,5,1,1450000',
  '2026-01-16,line,10,4,1,1450000',
].join('\n');

export const OutcomeImportPanel: React.FC<Props> = ({ workspaceId, canEdit }) => {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const [filename, setFilename] = useState('');
  const [rows, setRows] = useState<ParsedOutcomeRow[]>([]);
  const [problems, setProblems] = useState<RowProblem[]>([]);
  const [fatal, setFatal] = useState<string | null>(null);
  const [period, setPeriod] = useState<{ start: string; end: string } | null>(null);
  const [existing, setExisting] = useState<ExistingCoverage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [history, setHistory] = useState<OutcomeImportRecord[]>([]);
  const [stored, setStored] = useState<ExistingCoverage | null>(null);
  const [breakdown, setBreakdown] = useState<OutcomePlatformLine[]>([]);
  /**
   * Set when either read FAILED, which is not the same as there being nothing
   * stored. An empty table reads as "your upload is gone" and a hidden block
   * reads as "you never uploaded" — both are reassuring, and both would be
   * lies. Kept apart from `error`, which belongs to the upload.
   */
  const [storedError, setStoredError] = useState<string | null>(null);

  /**
   * Everything that describes what is already in the table. Uploading and
   * undoing both change all three, so they refresh together rather than leaving
   * a stale panel next to a fresh one.
   */
  const loadStoredState = useCallback(async () => {
    const [hist, cov, split] = await Promise.all([
      fetchOutcomeImports(workspaceId, t),
      fetchExistingCoverage(workspaceId, null, t),
      fetchOutcomeBreakdown(workspaceId, t),
    ]);
    if (hist.success && hist.data) setHistory(hist.data);
    setStored(cov.success && cov.data ? cov.data : null);
    setBreakdown(split.success && split.data ? split.data : []);
    setStoredError(
      cov.success && split.success
        ? null
        : (cov.error ?? split.error ?? t('outcomeImport.readBreakdownFailed')),
    );
  }, [workspaceId, t]);

  useEffect(() => {
    void loadStoredState();
  }, [loadStoredState]);

  const reset = () => {
    setRows([]);
    setProblems([]);
    setFatal(null);
    setPeriod(null);
    setExisting(null);
    setError(null);
    setDone(null);
  };

  const handleFile = useCallback(
    async (file: File) => {
      reset();
      setFilename(file.name);
      const text = await file.text();
      const parsed = parseOutcomeCsv(text, t);
      setRows(parsed.rows);
      setProblems(parsed.problems);
      setFatal(parsed.fatal);
      setPeriod(parsed.period);

      // What is already stored for those days — asked only once the file has
      // told us which days it is about.
      if (parsed.period) {
        const cov = await fetchExistingCoverage(workspaceId, parsed.period, t);
        if (cov.success && cov.data) setExisting(cov.data);
      }
    },
    [workspaceId, t],
  );

  const apply = useCallback(async () => {
    if (rows.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await applyOutcomeImport(workspaceId, filename, rows, t);
      if (!res.success) {
        setError(res.error ?? t('outcomeImport.importFailed'));
        return;
      }
      setDone(t('outcomeImport.applied', { count: new Set(rows.map((r) => r.date)).size }));
      setRows([]);
      setProblems([]);
      setPeriod(null);
      setExisting(null);
      await loadStoredState();
    } catch (err) {
      logError('OutcomeImportPanel', 'apply failed', err);
      setError(t('outcomeImport.importFailed'));
    } finally {
      setBusy(false);
    }
  }, [rows, busy, workspaceId, filename, t, loadStoredState]);

  const revert = useCallback(
    async (batchId: string) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const res = await revertOutcomeImport(batchId, t);
        if (!res.success) setError(res.error ?? t('outcomeImport.revertFailed'));
        else setDone(t('outcomeImport.reverted'));
        await loadStoredState();
      } finally {
        setBusy(false);
      }
    },
    [busy, t, loadStoredState],
  );

  const downloadSample = () => {
    // A BOM so Excel opens it as UTF-8 rather than guessing — the sample is
    // also the thing people copy their real file's shape from.
    const blob = new Blob(['\uFEFF' + SAMPLE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'outcomes-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const canApply = canEdit && rows.length > 0 && !fatal && !busy;

  return (
    <section className="bg-white rounded-xl border border-neutral-200">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h2 className="text-sm font-semibold text-ink-900">{t('outcomeImport.title')}</h2>
        <p className="mt-0.5 text-xs text-neutral-700">{t('outcomeImport.intro')}</p>
      </div>

      {/* The workspace is named ONCE by the frame around both panels on
          /settings/data, where it used to be announced separately by each and
          so appeared twice on one screen. The guarantee that made it worth
          saying at all still holds — a platform master can write into any
          workspace, so "which client is this" must not depend on remembering
          what was selected on another page. */}

      <div className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            id="outcome-csv"
            disabled={!canEdit}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          <label
            htmlFor="outcome-csv"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white ${
              canEdit ? 'cursor-pointer bg-ink-950 hover:bg-ink-900' : 'cursor-not-allowed bg-neutral-300'
            }`}
          >
            <Upload className="h-4 w-4" aria-hidden="true" /> {t('outcomeImport.chooseFile')}
          </label>
          <button
            type="button"
            onClick={downloadSample}
            className="flex items-center gap-1.5 rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-ink-700 hover:bg-neutral-50"
          >
            <Download className="h-4 w-4" aria-hidden="true" /> {t('outcomeImport.sampleFile')}
          </button>
          {filename && <span className="text-sm text-neutral-700">{filename}</span>}
        </div>

        <p className="text-xs text-neutral-700">
          {t('outcomeImport.columnHint', { metrics: METRIC_COLUMNS.join(', ') })}
        </p>

        {fatal && (
          <p role="alert" className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-800">
            {fatal}
          </p>
        )}

        {/* The span, read out of the file rather than typed into a picker. */}
        {period && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
            <p className="flex items-center gap-2 font-medium text-ink-900">
              <CalendarRange className="h-4 w-4 text-neutral-700" aria-hidden="true" />
              {t('outcomeImport.covers', {
                from: period.start,
                to: period.end,
                days: new Set(rows.map((r) => r.date)).size,
              })}
            </p>
            {/* Said before committing, because applying REPLACES these days. */}
            {existing && existing.days > 0 && (
              <p className="mt-1.5 text-warning-800">
                {t('outcomeImport.willReplace', { days: existing.days })}
              </p>
            )}
          </div>
        )}

        {problems.length > 0 && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-warning-900">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              {t('outcomeImport.problemCount', { count: problems.length })}
            </p>
            {/* Every one names its line and column: the reader has to find it in
                a spreadsheet, and "invalid input" cannot be acted on. */}
            <ul className="mt-2 space-y-1 text-sm text-warning-800">
              {problems.slice(0, 12).map((p, i) => (
                <li key={`${p.line}-${p.column}-${i}`}>
                  <span className="font-mono text-xs">
                    {t('outcomeImport.atLine', { line: p.line, column: p.column })}
                  </span>{' '}
                  {p.message}
                </li>
              ))}
            </ul>
            {problems.length > 12 && (
              <p className="mt-1.5 text-xs text-warning-700">
                {t('outcomeImport.andMore', { count: problems.length - 12 })}
              </p>
            )}
            {rows.length > 0 && (
              <p className="mt-2 text-xs text-warning-700">{t('outcomeImport.restStillUsable')}</p>
            )}
          </div>
        )}

        {rows.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium text-ink-900">
              {t('outcomeImport.previewTitle', { count: rows.length })}
            </p>
            <div className="overflow-x-auto rounded-lg border border-neutral-200">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-700">
                  <tr>
                    <th className="px-3 py-2 text-left">date</th>
                    <th className="px-3 py-2 text-left">platform</th>
                    {METRIC_COLUMNS.map((m) => (
                      <th key={m} className="px-3 py-2 text-right">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 8).map((r) => (
                    <tr key={`${r.date}-${r.platform ?? 'total'}`} className="border-t border-neutral-100">
                      <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                      <td className="px-3 py-1.5 text-neutral-700">
                        {r.platform ?? t('outcomeImport.wholeDay')}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.leads ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.bookings ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.sales ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.revenue ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 8 && (
              <p className="mt-1.5 text-xs text-neutral-700">
                {t('outcomeImport.previewRest', { count: rows.length - 8 })}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void apply()}
            disabled={!canApply}
            className={`rounded-md px-4 py-2 text-sm font-semibold text-white ${
              canApply ? 'bg-primary-600 hover:bg-primary-700' : 'cursor-not-allowed bg-neutral-300'
            }`}
          >
            {busy ? t('outcomeImport.saving') : t('outcomeImport.apply')}
          </button>
          {!canEdit && <span className="text-xs text-neutral-700">{t('outcomeImport.permission')}</span>}
        </div>

        {error && (
          <p role="alert" className="text-sm text-error-700">
            {error}
          </p>
        )}
        {done && (
          <p role="status" className="flex items-center gap-2 text-sm text-success-700">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> {done}
          </p>
        )}
      </div>

      {(storedError || (stored && stored.days > 0)) && (
        <div className="border-t border-neutral-100 p-4">
          <h3 className="text-sm font-semibold text-ink-900">{t('outcomeImport.storedTitle')}</h3>
          {storedError ? (
            <p role="alert" className="mt-2 flex items-center gap-2 text-sm text-error-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" /> {storedError}
            </p>
          ) : (
            <>
          <p className="mt-0.5 text-sm text-neutral-700">
            {t('outcomeImport.storedSpan', {
              count: stored?.days ?? 0,
              from: stored?.firstDate,
              to: stored?.lastDate,
            })}
          </p>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-700">
                  <th scope="col" className="py-1.5 pr-3 font-semibold">{t('outcomeImport.colChannel')}</th>
                  <th scope="col" className="py-1.5 px-3 text-right font-semibold">{t('outcomeImport.colDays')}</th>
                  <th scope="col" className="py-1.5 px-3 text-right font-semibold">{t('outcomeImport.colLeads')}</th>
                  <th scope="col" className="py-1.5 px-3 text-right font-semibold">{t('outcomeImport.colBookings')}</th>
                  <th scope="col" className="py-1.5 px-3 text-right font-semibold">{t('outcomeImport.colSales')}</th>
                  <th scope="col" className="py-1.5 pl-3 text-right font-semibold">{t('outcomeImport.colRevenue')}</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((line) => {
                  const missing = t('outcomeImport.notReported');
                  return (
                    <tr
                      key={line.platform ?? '*total*'}
                      data-outcome-line={line.platform ?? '*total*'}
                      className="border-b border-neutral-100 last:border-0"
                    >
                      {/* A whole-day row is a different KIND of row, not a row
                          with the channel left blank — so it is named, not
                          rendered as an empty cell. */}
                      <td className="py-1.5 pr-3 text-ink-900">
                        {line.platform ?? (
                          <span className="italic text-neutral-700">{t('outcomeImport.wholeDay')}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-neutral-700">{count(line.days)}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{reported(line.leads, count, missing)}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{reported(line.bookings, count, missing)}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{reported(line.sales, count, missing)}</td>
                      <td className="py-1.5 pl-3 text-right tabular-nums">{reported(line.revenue, baht, missing)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Never without this line. A per-channel table on a marketing page
              reads as "this is where the business came from", and the client's
              split is last-click or a how-did-you-hear field — precisely the
              attribution a mix model exists to second-guess. */}
          <p className="mt-3 flex gap-2 rounded-lg bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-700">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t('outcomeImport.asReportedNote')}</span>
          </p>
            </>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="border-t border-neutral-100 p-4">
          <h3 className="text-sm font-semibold text-ink-900">{t('outcomeImport.history')}</h3>
          <ul className="mt-2 space-y-2">
            {history.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-ink-900">{h.filename}</span>
                <span className="text-neutral-700">
                  {h.periodStart} – {h.periodEnd} · {t('outcomeImport.historyRows', { count: h.rowCount })}
                </span>
                {h.status === 'reverted' ? (
                  <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-700">
                    {t('outcomeImport.wasReverted')}
                  </span>
                ) : (
                  canEdit && (
                    <button
                      type="button"
                      onClick={() => void revert(h.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-xs text-neutral-700 underline hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Undo2 className="h-3 w-3" aria-hidden="true" /> {t('outcomeImport.revert')}
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default OutcomeImportPanel;
